import { FC } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface ExpenseDialogsProps {
  expense: any;
  isEditDialogOpen: boolean;
  isDeleteDialogOpen: boolean;
  setIsEditDialogOpen: (open: boolean) => void;
  setIsDeleteDialogOpen: (open: boolean) => void;
  editedExpense: any;
  setEditedExpense: (expense: any) => void;
  onUpdate: () => void;
}

export const ExpenseDialogs: FC<ExpenseDialogsProps> = ({
  expense,
  isEditDialogOpen,
  isDeleteDialogOpen,
  setIsEditDialogOpen,
  setIsDeleteDialogOpen,
  editedExpense,
  setEditedExpense,
  onUpdate
}) => {
  const handleEditSubmit = async () => {
    try {
      const { error } = await supabase
        .from('platform_expenses')
        .update(editedExpense)
        .eq('id', expense.id);

      if (error) {
        console.error('Error updating expense:', error);
        toast.error('Error al actualizar el gasto');
        throw error;
      }

      toast.success('Gasto actualizado correctamente');
      onUpdate();
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Error in handleEditSubmit:', error);
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('platform_expenses')
        .delete()
        .eq('id', expense.id);

      if (error) {
        console.error('Error deleting expense:', error);
        toast.error('Error al eliminar el gasto');
        throw error;
      }

      toast.success('Gasto eliminado correctamente');
      onUpdate();
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error in handleDelete:', error);
    }
  };

  return (
    <>
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-[95vw] w-full sm:max-w-[600px] max-h-[90vh] p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Editar Gasto</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[calc(90vh-120px)] px-6 pb-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre</Label>
                <Input
                  id="name"
                  value={editedExpense.name}
                  onChange={(e) => setEditedExpense({ ...editedExpense, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="amount">Monto</Label>
                <Input
                  id="amount"
                  type="number"
                  value={editedExpense.amount}
                  onChange={(e) => setEditedExpense({ ...editedExpense, amount: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="type">Tipo</Label>
                <Select
                  value={editedExpense.type}
                  onValueChange={(value) => setEditedExpense({ ...editedExpense, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona el tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fijo</SelectItem>
                    <SelectItem value="variable">Variable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="category">Categoría</Label>
                <Select
                  value={editedExpense.category}
                  onValueChange={(value) => setEditedExpense({ ...editedExpense, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona la categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hosting">Hosting</SelectItem>
                    <SelectItem value="software">Software</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="salaries">Salarios</SelectItem>
                    <SelectItem value="other">Otros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={editedExpense.description || ''}
                  onChange={(e) => setEditedExpense({ ...editedExpense, description: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="due_date">Fecha de vencimiento</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={editedExpense.due_date ? editedExpense.due_date.split('T')[0] : ''}
                  onChange={(e) => setEditedExpense({ ...editedExpense, due_date: e.target.value })}
                />
              </div>
              {editedExpense.is_paid && (
                <div>
                  <Label htmlFor="payment_date">Fecha de pago</Label>
                  <Input
                    id="payment_date"
                    type="date"
                    value={editedExpense.payment_date ? editedExpense.payment_date.split('T')[0] : ''}
                    onChange={(e) => setEditedExpense({ ...editedExpense, payment_date: e.target.value })}
                  />
                </div>
              )}
              <div>
                <Label htmlFor="payment_reference">Referencia de pago</Label>
                <Input
                  id="payment_reference"
                  value={editedExpense.payment_reference || ''}
                  onChange={(e) => setEditedExpense({ ...editedExpense, payment_reference: e.target.value })}
                />
              </div>
            </div>
          </ScrollArea>
          <DialogFooter className="p-6 pt-0">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEditSubmit}>
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente este gasto.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};